import { useMemo } from 'react';
import { css } from '@emotion/react';
import { ScheduleActivityHistoryType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule_activity.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { UsersQueries } from '@frontend/api-users';
import { useTranslation } from '@frontend/i18n';
import { Avatar, SpinningLoader, Text } from '@frontend/design-system';
import { formatDateByRecency } from '../../../utils/date-helpers';
import { getActivityTitle, getActorLabel, isAiActivity } from './helpers';

type Props = { locationId: string; referenceId: string };

export const ActivityHistory = ({ locationId, referenceId }: Props) => {
  const { t } = useTranslation('scheduleCalendarEvents');
  const locationUsers = UsersQueries.useGetMultiLocationsUsers({
    locationIds: [locationId],
    params: { showChildren: false },
  }).flatMap(({ data }) => data || []);

  const { data, isLoading, isError } = SchedulerV3.Queries.useListScheduleActivityHistoryQuery({
    request: {
      locationId,
      appointmentId: referenceId,
      historyType: ScheduleActivityHistoryType.SCHEDULE_ACTIVITY_HISTORY_TYPE_CALENDAR_EVENT,
    },
    options: {
      enabled: !!(locationId && referenceId),
    },
  });

  const historyItems = useMemo(() => data?.history ?? [], [data]);

  if (isLoading) {
    return <SpinningLoader size='xs' />;
  }

  if (isError) {
    return (
      <div css={containerStyles}>
        <Text size='small' color='error'>
          {t('Error loading activity history')}
        </Text>
      </div>
    );
  }

  if (!historyItems.length) {
    return null;
  }

  return (
    <section css={containerStyles}>
      {historyItems.map((item, index) => {
        const isLastItem = index === historyItems.length - 1;
        const title = getActivityTitle(item);
        const actorLabel = getActorLabel(item, locationUsers);
        const isAiSource = isAiActivity(item.activitySourceType);

        return (
          <div key={item.id} css={rowStyles}>
            <div css={timelineColumnStyles}>
              {!isLastItem && <div css={timelineLineStyles} />}
              <div css={timelineDotStyles} />
            </div>
            <div css={contentStyles}>
              <Text weight='bold' size='large' css={titleStyles}>
                {title}
              </Text>
              <div css={metaRowStyles}>
                <Avatar name={actorLabel} size='small' isAiAssistant={isAiSource} />
                <div css={metaTextStyles}>
                  <Text size='medium' css={actorStyles}>
                    {actorLabel}
                  </Text>
                  <Text size='small' color='subdued'>
                    {formatDateByRecency(item.occurredAt)}
                  </Text>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--fabric-space-gap-xs)',
  maxHeight: '260px',
  overflowY: 'auto',
});

const rowStyles = css({
  display: 'grid',
  gridTemplateColumns: '20px minmax(0, 1fr)',
  columnGap: 'var(--fabric-space-gap-xs)',
});

const timelineColumnStyles = css({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  paddingTop: 'var(--fabric-space-gap-xxs)',
});

const timelineLineStyles = css({
  position: 'absolute',
  top: 'var(--fabric-space-gap-md)',
  bottom: `calc(-1 * var(--fabric-space-gap-sm))`,
  width: '1px',
  backgroundColor: 'var(--fabric-color-neutral-alpha-100-alpha-60)',
});

const timelineDotStyles = css({
  position: 'relative',
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: 'var(--fabric-color-neutral-alpha-100-alpha-60)',
});

const contentStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--fabric-space-gap-xs)',
  paddingBottom: 'var(--fabric-space-gap-sm)',
});

const titleStyles = css({
  lineHeight: 1.2,
});

const metaRowStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--fabric-space-gap-sm)',
});

const metaTextStyles = css({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});

const actorStyles = css({
  lineHeight: 1.2,
});
